/**
 * THE REACT BINDING FOR SIGN-IN — and the reason the shell needs one at all.
 * ========================================================================
 *
 * `src/auth/` is built, tested and installed at startup, and until this file existed **none of it
 * reached the screen**: `SignInBanner` was exported and never mounted, so a technician got no sign-in
 * affordance and no session feedback whatsoever. The banner is not the interesting part — the seven
 * phases and their wording are (`src/auth/phase.ts`) — and this hook is the only thing between them and
 * `AppShell`.
 *
 * ⚠️ **THERE IS NO PLUMBING THROUGH `main.tsx`, DELIBERATELY.** `installOidcAuth()` runs before the first
 * paint and installs its {@link OidcAuth} through `setTokenProvider()`. That binding — `core/auth.ts`'s
 * `tokenProvider()` — is already the app's one seam for "who issues tokens", it is set exactly once, and
 * it is what `api.ts` and `sync.ts` read. Threading an `InstallResult` down through `App` → `AppShell`
 * would create a SECOND source of truth for the same fact, which is how a UI ends up reporting a session
 * the transport does not have. So this hook reads the seam.
 *
 * WHAT THE PROVIDER'S `kind` DECIDES, AND WHY IT IS NOT A FALLBACK CHAIN
 * ---------------------------------------------------------------------
 * `installOidcAuth` does **not** call `setTokenProvider` when the OIDC configuration is incomplete —
 * see its header: the stub stays active and the app sends nothing rather than firing requests that would
 * all come back 401 and read like an expired session. So the provider's `kind` is a faithful report of
 * which world we are in, and there are exactly two:
 *
 *  · `'oidc-pkce'` → a real provider. Its own {@link AuthPhase} is the truth; subscribe to it.
 *  · `'stub'`      → **no sign-in exists in this build.** That is `not-configured`, and this hook says
 *                    so in the phase system's own terms rather than inventing a third state.
 *
 * The `not-configured` `detail` is READ, not guessed: `readOidcConfig` is the same pure function
 * `install.ts` calls, over the same `import.meta.env`, so the variable names named here are the variable
 * names that are actually missing. A hand-written "sign-in is not set up" string would drift the moment
 * one of the five was supplied.
 *
 * ⚠️ **THIS HOOK NEVER TOUCHES THE OUTBOX, AND MUST NOT.** `src/auth/` contains no import of `outbox.ts`
 * at all (a test in `test/auth/config-and-controls.test.ts` enforces it), because requirement 4 in
 * `core/auth.ts` is that a sign-in problem must not cost a technician their work. A hook that "tidied up"
 * queued rows on `session-expired` would defeat the entire module, so it holds nothing but a phase.
 */

import { useEffect, useState } from 'react';
import { readOidcConfig } from '../auth/config';
import type { AuthPhase } from '../auth/phase';
import { tokenProvider } from '../core/auth';
import type { TokenProvider } from '../core/auth';

/**
 * The part of `OidcAuth` a UI needs, stated structurally rather than as `instanceof OidcAuth`.
 *
 * Structural on purpose: `instanceof` against a class is a promise about module identity, and it makes
 * the only way to exercise this hook "construct a real OIDC client and drive a fake token endpoint" —
 * i.e. it pushes every UI test through the protocol. `core/auth.ts`'s seam is already an interface, and
 * this is the same idea one level up.
 */
export interface AuthPhaseSource extends TokenProvider {
  phase(): AuthPhase;
  /** Calls back immediately with the current phase, then on every transition. Returns an unsubscribe. */
  subscribe(listener: (phase: AuthPhase) => void): () => void;
  beginSignIn(options?: { readonly navigate?: boolean }): Promise<unknown>;
}

export interface AuthBinding {
  /**
   * Never `null`. A missing provider is a phase (`not-configured`) rather than an absence, because an
   * absence renders as nothing at all — which is the defect this file was written to fix.
   */
  readonly phase: AuthPhase;
  /** The live provider, so the shell can tell "not built" from "signed out". */
  readonly provider: TokenProvider;
  /**
   * Starts the redirect. **Absent whenever there is no provider that could start one.**
   *
   * Note what this is NOT: it is not the decision about whether to OFFER sign-in. That is
   * `describeAuthPhase(...).canStartSignIn`, which is `false` for every offline first sign-in — there is
   * no authenticating in a dead zone, the redirect and the passkey check both need a network, and a
   * button there produces a blank browser tab in a locker bay. `SignInBanner` requires both.
   */
  readonly signIn?: () => void;
}

/**
 * The phase for a build with no installed provider, with the missing variables named.
 *
 * Computed once per mount rather than per render: `import.meta.env` is fixed at build time and the
 * provider binding is set once at startup, so re-reading it on every render would be pure waste.
 */
function phaseWithNoProvider(): AuthPhase {
  const result = readOidcConfig(
    (import.meta.env ?? {}) as Readonly<Record<string, unknown>>,
    globalThis.location?.origin,
  );
  return {
    kind: 'not-configured',
    detail: result.ok
      ? // Configuration IS present and the provider still is not installed, so `installOidcAuth()` did
        // not run (or ran twice — see its `provider-already-set` branch). A startup bug, not a
        // configuration one, and the two must not report the same string.
        'configured but not installed'
      : result.missing.map((entry) => entry.variable).join(', '),
  };
}

/** `null` when this provider cannot report a phase. Loud, because a silent `null` renders as nothing. */
function asPhaseSource(provider: TokenProvider): AuthPhaseSource | null {
  if (provider.kind === 'stub') {
    return null;
  }
  const candidate = provider as Partial<AuthPhaseSource>;
  if (
    typeof candidate.phase === 'function' &&
    typeof candidate.subscribe === 'function' &&
    typeof candidate.beginSignIn === 'function'
  ) {
    return provider as AuthPhaseSource;
  }
  // eslint-disable-next-line no-console -- a real provider that cannot report a phase would leave the
  // shell with nothing to say about sign-in, which is precisely the invisible failure being fixed here.
  console.error(
    `[lockercustody] the installed token provider (${provider.kind}) reports no sign-in phase; ` +
      'the shell will describe sign-in as not configured',
  );
  return null;
}

/**
 * The current {@link AuthPhase}, kept in step with the provider, plus the one action a UI may start.
 *
 * The subscription is torn down on unmount. Without that, `OidcAuth`'s listener set holds a `setState`
 * for a dead component and every proactive refresh — one per 15-minute token, dozens a shift — calls it.
 */
export function useAuthPhase(): AuthBinding {
  // Read once. `setTokenProvider` throws on a second call and `main.tsx` awaits `installOidcAuth()`
  // before the first render, so the answer cannot change under a mounted shell.
  const [provider] = useState<TokenProvider>(() => tokenProvider());
  const [source] = useState<AuthPhaseSource | null>(() => asPhaseSource(provider));
  const [phase, setPhase] = useState<AuthPhase>(() => source?.phase() ?? phaseWithNoProvider());

  useEffect(() => {
    if (source === null) {
      return undefined;
    }
    return source.subscribe(setPhase);
  }, [source]);

  return {
    phase,
    provider,
    ...(source === null
      ? {}
      : {
          signIn: (): void => {
            // `void`: the promise resolves with the authorization URL, and the navigation it triggers
            // is the point. A rejection is impossible — `beginSignIn` reports failure as a phase
            // (`sign-in-failed`), which is how the technician hears about it.
            void source.beginSignIn();
          },
        }),
  };
}
